import { useEffect, useMemo } from 'react'

import { AppId } from '@inno-tools/container-bridge'
import { useGetAppsQuery } from 'shared/api/containerApi'
import { appConfigs } from 'shared/configs/appConfigs'
import { publicAppConfigs } from 'shared/configs/publicAppConfigs'
import { useRouteCheck } from 'shared/hooks/useRouteCheck'
import { logger } from 'shared/services/logger'
import { AppConfig } from 'shared/types/app'

import { hasAppAccess } from './hasAppAccess'

export type AvailableApps = Record<AppId, AppConfig>

type AvailabilityObservation = {
  availableAppIds: string[]
  visibleAppIds: string[]
  disabledAppIds: string[]
  fingerprint: string
}

const observationsBySession = new Map<string, AvailabilityObservation>()
const MAX_TRACKED_SESSIONS = 10

function sortedIds(apps: AppConfig[]): string[] {
  return apps.map(app => String(app.id)).sort()
}

function difference(next: string[], previous: string[]): string[] {
  const previousSet = new Set(previous)
  return next.filter(id => !previousSet.has(id))
}

function reportAvailability(apps: AvailableApps, isPublicRoute: boolean): void {
  const allVisibleApps = Object.values(apps)
  const visibleAppIds = sortedIds(allVisibleApps)
  const disabledAppIds = sortedIds(allVisibleApps.filter(app => app.isDisabled))
  const availableAppIds = sortedIds(
    allVisibleApps.filter(app => !app.isDisabled)
  )
  const configuredAppIds = Object.keys(appConfigs).sort()
  const fingerprint = JSON.stringify({
    availableAppIds,
    visibleAppIds,
    disabledAppIds,
    isPublicRoute
  })
  const sessionId = logger.getSnapshot().sessionId
  const previous = observationsBySession.get(sessionId)
  if (previous?.fingerprint === fingerprint) return

  const commonPayload = {
    availableAppIds,
    visibleAppIds,
    disabledAppIds,
    configuredAppIds,
    availableCount: availableAppIds.length,
    visibleCount: visibleAppIds.length,
    disabledCount: disabledAppIds.length,
    configuredCount: configuredAppIds.length,
    unavailableCount: Math.max(
      0,
      configuredAppIds.length - visibleAppIds.length
    ),
    source: isPublicRoute ? 'public_route' : 'apps_api'
  }

  if (previous) {
    logger.track('app_registry.availability_changed', {
      ...commonPayload,
      addedAppIds: difference(availableAppIds, previous.availableAppIds),
      removedAppIds: difference(previous.availableAppIds, availableAppIds),
      newlyDisabledAppIds: disabledAppIds.filter(id =>
        previous.availableAppIds.includes(id)
      ),
      newlyEnabledAppIds: availableAppIds.filter(id =>
        previous.disabledAppIds.includes(id)
      )
    })
  } else {
    logger.track('app_registry.availability_resolved', commonPayload)
  }

  observationsBySession.set(sessionId, {
    availableAppIds,
    visibleAppIds,
    disabledAppIds,
    fingerprint
  })
  if (observationsBySession.size > MAX_TRACKED_SESSIONS) {
    const oldestSessionId = observationsBySession.keys().next().value
    if (oldestSessionId) observationsBySession.delete(oldestSessionId)
  }
}

export const useAvailableApps = (): {
  apps: AvailableApps
  isLoading: boolean
} => {
  const {
    data: containerAppsData = [],
    isLoading,
    fetchStatus,
    isError,
    isFetched
  } = useGetAppsQuery()

  const isPublicRoute = useRouteCheck('/public')

  const result = useMemo(() => {
    const apps = Object.entries(appConfigs).reduce<AppConfig[]>(
      (acc, [_, config]) => {
        const appAccessData = containerAppsData.find(
          data => config.id === data.app_id
        )

        const hasPublicPage = Object.values(publicAppConfigs).some(
          c => `${c.id}` === `${config.id}`
        )

        const isAppAccessible = hasAppAccess(
          String(config.id),
          containerAppsData
        )
        const isAppVisible = !!appAccessData || (isPublicRoute && hasPublicPage)
        const isAppDisabled = !!appAccessData && !isAppAccessible

        if (isAppVisible) {
          acc.push({
            ...config,
            isDisabled: isAppDisabled,
            isSpotlighted: !!appAccessData?.is_spotlighted,
            name: appAccessData?.name || config.name,
            label: appAccessData?.label || config.label
          })
        } else if (isLoading && fetchStatus !== 'idle') {
          acc.push({
            ...config,
            isDisabled: true
          })
        }

        return acc
      },
      []
    )

    return {
      apps: Object.fromEntries(apps.map(app => [app.id, app])) as AvailableApps,
      // https://github.com/TanStack/query/issues/3584#issuecomment-1262599089
      isLoading: isLoading && fetchStatus !== 'idle'
    }
  }, [containerAppsData, isLoading, fetchStatus, isPublicRoute])

  useEffect(() => {
    if (!result.isLoading && !isError && (isFetched || isPublicRoute)) {
      reportAvailability(result.apps, isPublicRoute)
    }
  }, [isError, isFetched, isPublicRoute, result.apps, result.isLoading])

  return result
}
